<script lang="ts">
  import Dialog from './Dialog.svelte';
  import Icon from './Icon.svelte';
  import { photoUrl } from './api';
  import { formatDistanceM } from '~/shared/group-actions.js';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import type { PoiView, GroupView } from '~/shared/api.js';

  let {
    title,
    subtitle,
    results,
    groups,
    onPick,
    onClose,
    onAddPlace,
  }: {
    title: string;
    subtitle: string;
    results: { poi: PoiView; distanceM: number }[];
    groups: GroupView[];
    onPick: (result: { poi: PoiView; distanceM: number }) => void;
    onClose: () => void;
    onAddPlace?: () => void;
  } = $props();

  const groupsById = $derived(new Map(groups.map((g) => [g.id, g])));
</script>

<Dialog {onClose} variant="sheet">
  <div class="sheet" role="dialog" aria-modal="true" aria-label={title}>
    <header class="head">
      <span class="head-title">
        <Icon name="sparkles" size={14} strokeWidth={2.25} />
        <span class="head-text">
          <h2>{title}</h2>
          <span class="head-sub">{subtitle}</span>
        </span>
      </span>
      <button class="close-btn" type="button" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={18} />
      </button>
    </header>

    {#if results.length === 0}
      <div class="empty">
        <p>No nearby places yet — bookmark more!</p>
        {#if onAddPlace}
          <button type="button" class="btn btn-tertiary add-place" onclick={onAddPlace}>
            Add a place
          </button>
        {/if}
      </div>
    {:else}
      <ul class="rows">
        {#each results as r (r.poi.id)}
          {@const src = photoUrl(r.poi.photoReference, 96)}
          {@const displayName = r.poi.customName ?? r.poi.name}
          <li>
            <button type="button" class="row" onclick={() => onPick(r)}>
              <div class="thumb">
                {#if src}
                  <img src={src} alt="" loading="lazy" />
                {:else}
                  <div class="thumb-blank"></div>
                {/if}
              </div>
              <div class="text">
                <span class="name">{displayName}</span>
                <span class="meta">
                  <span class="distance">{formatDistanceM(r.distanceM)}</span>
                  {#if r.poi.rating !== null}
                    <span aria-hidden="true">·</span>
                    <span class="rating">
                      <Icon name="star" size={11} />
                      <span class="tabular-nums">{r.poi.rating.toFixed(1)}</span>
                    </span>
                  {/if}
                  {#if r.poi.category}
                    <span aria-hidden="true">·</span>
                    <span class="category">{formatTypeLabel(r.poi.category)}</span>
                  {/if}
                </span>
                {#if r.poi.groupIds.length > 0}
                  <span class="group-dots">
                    {#each r.poi.groupIds as gid (gid)}
                      {@const g = groupsById.get(gid)}
                      {#if g}
                        <span class="dot dot--{g.tint}" title={g.name}></span>
                      {/if}
                    {/each}
                  </span>
                {/if}
              </div>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</Dialog>

<style>
  .sheet {
    position: relative;
    background: var(--color-canvas);
    width: 100%;
    max-width: 580px;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    box-shadow: rgba(15, 15, 15, 0.2) 0 -8px 32px -4px;
    display: flex;
    flex-direction: column;
    max-height: calc(100dvh - 56px);
    overflow: hidden;
  }
  @media (min-width: 768px) {
    .sheet {
      border-radius: var(--radius-md);
      max-height: 80vh;
      box-shadow:
        rgba(0, 0, 0, 0.02) 0 0 0 1px,
        rgba(0, 0, 0, 0.04) 0 2px 6px 0,
        rgba(0, 0, 0, 0.10) 0 4px 8px 0;
    }
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem 0.75rem;
    border-bottom: 1px solid var(--color-hairline-soft);
    flex: none;
  }
  .head-title { display: inline-flex; align-items: center; gap: 0.5rem; color: var(--color-ink); min-width: 0; }
  .head-text { display: flex; flex-direction: column; min-width: 0; }
  .head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .head-sub { font-size: 12px; color: var(--color-muted); }
  .close-btn {
    background: transparent;
    border: 0;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-muted);
  }
  .close-btn:hover { background: var(--color-surface-soft); color: var(--color-ink); }

  .empty {
    padding: 2.5rem 1.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.875rem;
    color: var(--color-muted);
    text-align: center;
  }
  .empty p { margin: 0; font-size: 14px; }
  .add-place { padding: 0.5rem 0.875rem; min-height: 0; height: auto; font-size: 13px; }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0.25rem 0;
    overflow-y: auto;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    width: 100%;
    background: transparent;
    border: 0;
    padding: 0.625rem 1.25rem;
    text-align: left;
    cursor: pointer;
    color: var(--color-ink);
    font: inherit;
    min-height: 56px;
  }
  .row:hover, .row:focus-visible {
    background: var(--color-surface-soft);
    outline: none;
  }
  .thumb {
    width: 48px;
    height: 48px;
    flex: none;
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--color-surface-strong);
  }
  .thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .thumb-blank { width: 100%; height: 100%; }
  .text { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
  .name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem;
    font-size: 12px;
    color: var(--color-muted);
  }
  .distance { font-weight: 500; color: var(--color-ink); }
  .rating {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: var(--color-ink);
  }
  .group-dots { display: inline-flex; gap: 4px; margin-top: 2px; }
  .group-dots .dot { width: 6px; height: 6px; }
</style>
